// @ts-nocheck
import {
  Alert,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { IconLock, IconShieldCheck } from '@tabler/icons-react';
// hasAccess is a pure function (no SDK hooks) — safe to call live.
// AccessControl is imported for documentation/type reference only; it calls
// useAuth() on mount, so it is NOT rendered live on this page.
import { hasAccess } from '@/components/security';

/**
 * CCMS Demo — Access Control showcase.
 *
 * Documents the SDK-bound `AccessControl` component (doc-only, because it calls
 * `useAuth()` on mount and needs the CCMS provider) and the pure `hasAccess`
 * helper (rendered live with inline mock roles/permissions).
 */
export default function CcmsAccessControlPage() {
  // ---- Live demo data for the pure hasAccess() helper -----------------------
  const mockUserRoles = ['Member', 'Editor'];
  const mockUserPermissions = ['content.read', 'content.write'];
  const mockUserDomains = ['ccms.crm'];

  const liveChecks: Array<{ label: string; rule: any; description: string }> = [
    {
      label: 'Editor can write content',
      rule: { permissions: ['content.write'] },
      description: "User has 'content.write' permission",
    },
    {
      label: 'Requires Admin role',
      rule: { roles: ['Admin'], strictAccess: true },
      description: "strictAccess: no Admin bypass, user is not Admin",
    },
    {
      label: 'Any of Editor / Manager',
      rule: { roles: ['Editor', 'Manager'] },
      description: 'requireAll defaults to false → any match passes',
    },
    {
      label: 'Requires ALL permissions',
      rule: { permissions: ['content.read', 'content.delete'], requireAll: true },
      description: "User lacks 'content.delete'",
    },
    {
      label: 'Scoped to CRM domain',
      rule: { domains: ['ccms.crm'] },
      description: "User belongs to 'ccms.crm' domain",
    },
    {
      label: 'No rule supplied',
      rule: undefined,
      description: 'hasAccess(...) returns true when rule is omitted',
    },
  ];

  return (
    <Stack gap="xl">
      {/* ----------------------------------------------------------------- */}
      {/* Header                                                            */}
      {/* ----------------------------------------------------------------- */}
      <Stack gap={4}>
        <Title order={2}>Access Control (AccessControl, hasAccess)</Title>
        <Text c="dimmed">
          Role / permission / domain gating utilities from{' '}
          <Code>@/components/security</Code>. <Code>AccessControl</Code> wraps UI
          and reads the signed-in user from the CCMS SDK; <Code>hasAccess</Code>{' '}
          is a pure predicate you can call anywhere. These components typically
          need CCMS SDK permissions for live gating.
        </Text>
      </Stack>

      <Alert
        variant="light"
        color="blue"
        icon={<IconShieldCheck size={18} />}
        title="What renders live on this page"
      >
        <Text size="sm">
          <Code>hasAccess</Code> is rendered live (it is a pure function — no
          hooks, no network). <Code>AccessControl</Code> is shown as documentation
          only because it calls <Code>useAuth()</Code> on mount and requires the
          CCMS SDK provider; rendering it without a provider would throw.
        </Text>
      </Alert>

      {/* ================================================================= */}
      {/* AccessControl — DOC ONLY                                          */}
      {/* ================================================================= */}
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Group gap="sm">
              <ThemeIcon size="lg" radius="md" variant="light" color="grape">
                <IconLock size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Title order={4}>AccessControl</Title>
                <Text size="sm" c="dimmed">
                  Conditionally render, disable, or blur children based on the
                  signed-in user's roles, permissions, and domains.
                </Text>
              </Stack>
            </Group>
            <Badge color="grape" variant="light">
              Doc only
            </Badge>
          </Group>

          <Divider label="Key props" labelPosition="left" />

          <Stack gap={2}>
            <Text size="sm">
              <Code>children</Code> — ReactNode — <b>required</b> (UI to gate)
            </Text>
            <Text size="sm">
              <Code>roles</Code> — string[] — optional (e.g. <Code>['Admin']</Code>)
            </Text>
            <Text size="sm">
              <Code>permissions</Code> — string[] — optional (maps to user{' '}
              <Code>rights</Code>)
            </Text>
            <Text size="sm">
              <Code>domains</Code> — string[] — optional
            </Text>
            <Text size="sm">
              <Code>requireAll</Code> — boolean — optional (require ALL vs ANY;
              default <Code>false</Code>)
            </Text>
            <Text size="sm">
              <Code>strictAccess</Code> — boolean — optional (no Admin bypass;
              default <Code>false</Code>)
            </Text>
            <Text size="sm">
              <Code>mode</Code> — 'hide' | 'disable' | 'blur' — optional (default{' '}
              <Code>'hide'</Code>)
            </Text>
            <Text size="sm">
              <Code>fallback</Code> — ReactNode — optional (shown when hidden)
            </Text>
            <Text size="sm">
              <Code>disabledMessage</Code> — string — optional (tooltip in{' '}
              <Code>disable</Code> mode)
            </Text>
          </Stack>

          <Divider label="Usage" labelPosition="left" />

          <Code block>{`import { AccessControl } from '@/components/security';

// Hide unless the user is an Admin
<AccessControl roles={['Admin']}>
  <AdminPanel />
</AccessControl>

// Disable (instead of hide) when the permission is missing
<AccessControl permissions={['content.write']} mode="disable">
  <Button>Edit</Button>
</AccessControl>

// Show a fallback when access is denied
<AccessControl roles={['Manager']} fallback={<UpgradePrompt />}>
  <ManagerDashboard />
</AccessControl>`}</Code>

          <Alert variant="light" color="yellow" title="Needs to run live">
            <Text size="sm">
              CCMS SDK permissions for live gating. <Code>AccessControl</Code>{' '}
              calls <Code>useAuth()</Code> internally, so it must be mounted under
              the CCMS SDK provider with a signed-in user. Not rendered here to
              keep the demo crash-free.
            </Text>
          </Alert>

          <Text size="xs" c="dimmed">
            Related (also SDK-backed, doc-only): <Code>useCanAccess(rule)</Code>,{' '}
            <Code>useAccessControl()</Code>, and the{' '}
            <Code>withAccessControl(Component, rule)</Code> HOC.
          </Text>
        </Stack>
      </Card>

      {/* ================================================================= */}
      {/* hasAccess — LIVE                                                  */}
      {/* ================================================================= */}
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Group gap="sm">
              <ThemeIcon size="lg" radius="md" variant="light" color="teal">
                <IconShieldCheck size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Title order={4}>hasAccess</Title>
                <Text size="sm" c="dimmed">
                  Pure predicate that evaluates an <Code>AccessRule</Code> against
                  explicit user roles, permissions, and domains. Powers both{' '}
                  <Code>AccessControl</Code> and the access hooks.
                </Text>
              </Stack>
            </Group>
            <Badge color="teal" variant="light">
              Live
            </Badge>
          </Group>

          <Divider label="Signature & params" labelPosition="left" />

          <Stack gap={2}>
            <Text size="sm">
              <Code>userRoles</Code> — string[] — optional (default <Code>[]</Code>)
            </Text>
            <Text size="sm">
              <Code>userPermissions</Code> — string[] — optional (default{' '}
              <Code>[]</Code>)
            </Text>
            <Text size="sm">
              <Code>userDomains</Code> — string[] — optional (default{' '}
              <Code>[]</Code>)
            </Text>
            <Text size="sm">
              <Code>rule</Code> — AccessRule — optional (returns <Code>true</Code>{' '}
              if omitted)
            </Text>
            <Text size="sm" c="dimmed">
              Returns <Code>boolean</Code>. Admin roles bypass unless{' '}
              <Code>strictAccess</Code> is set.
            </Text>
          </Stack>

          <Divider label="Usage" labelPosition="left" />

          <Code block>{`import { hasAccess, type AccessRule } from '@/components/security';

const rule: AccessRule = { permissions: ['content.write'] };

const canEdit = hasAccess(
  ['Member', 'Editor'],        // userRoles
  ['content.read', 'content.write'], // userPermissions
  ['ccms.crm'],                // userDomains
  rule,
); // → true`}</Code>

          <Alert variant="light" color="teal" title="Live evaluation">
            <Text size="sm" mb="xs">
              Evaluated below against this fixed mock user (no SDK / network):
            </Text>
            <Stack gap={2}>
              <Text size="sm">
                roles: <Code>{JSON.stringify(mockUserRoles)}</Code>
              </Text>
              <Text size="sm">
                permissions: <Code>{JSON.stringify(mockUserPermissions)}</Code>
              </Text>
              <Text size="sm">
                domains: <Code>{JSON.stringify(mockUserDomains)}</Code>
              </Text>
            </Stack>
          </Alert>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {liveChecks.map((check) => {
              const result = hasAccess(
                mockUserRoles,
                mockUserPermissions,
                mockUserDomains,
                check.rule
              );
              return (
                <Card key={check.label} withBorder padding="sm" radius="sm">
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={2}>
                      <Text size="sm" fw={600}>
                        {check.label}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {check.description}
                      </Text>
                      <Code>{`rule = ${JSON.stringify(check.rule)}`}</Code>
                    </Stack>
                    <Badge
                      color={result ? 'green' : 'red'}
                      variant="light"
                      style={{ flexShrink: 0 }}
                    >
                      {result ? 'allowed' : 'denied'}
                    </Badge>
                  </Group>
                </Card>
              );
            })}
          </SimpleGrid>

          <Alert variant="light" color="yellow" title="Needs to run live">
            <Text size="sm">
              CCMS SDK permissions for live gating. The pure function shown here
              uses inline mock arrays; in a real app you pass the SDK user's{' '}
              <Code>roles</Code> and <Code>rights</Code> (or use{' '}
              <Code>AccessControl</Code> / <Code>useCanAccess</Code>, which read
              them from the SDK for you).
            </Text>
          </Alert>
        </Stack>
      </Card>
    </Stack>
  );
}
